<script>
import { ref, watch, getCurrentInstance, onUnmounted } from 'vue'

export default {
    name: 'SliderCaptcha',
    props: {
        visible: {
            type: Boolean,
            default: false
        },
        phone: {
            type: String,
            default: ''
        }
    },
    emits: ['close', 'success', 'fail'],
    setup(props, { emit }) {
        const proxy = getCurrentInstance().proxy

        // 验证码数据
        const bgImage = ref('')
        const sliderImage = ref('')
        const sliderY = ref(0)
        const token = ref('')
        const loading = ref(false)

        // 滑块位置
        const sliderX = ref(0)
        // 前端显示区域308px，滑块图片52px，最大滑动距离 = 308 - 52 = 256
        const maxSliderX = ref(256)

        // 滑动状态
        const isDragging = ref(false)
        let startX = 0
        let startTime = 0

        // 滑动轨迹
        const tracks = ref([])

        // 验证状态: '' | 'success' | 'fail'
        const verifyStatus = ref('')
        const tipText = ref('')

        // 刷新次数限制
        const refreshCount = ref(0)
        const maxRefresh = 5

        /**
         * 获取验证码
         */
        const getCaptcha = async () => {
            if (loading.value) return

            loading.value = true
            verifyStatus.value = ''
            tipText.value = ''
            sliderX.value = 0
            tracks.value = []

            try {
                const res = await proxy.$http.get('/core/base/a/sliderCapt/generate')

                if (res.data.state === 0) {
                    const data = res.data.result
                    token.value = data.token
                    bgImage.value = data.bgImage
                    sliderImage.value = data.sliderImage
                    // 后端Y坐标基于150px高度，前端显示154px，需要缩放
                    // 再减去6px补偿滑块图片内部padding，使拼图内容与缺口对齐
                    sliderY.value = Math.round((data.sliderY || 0) * 154 / 150) - 6
                } else {
                    tipText.value = res.data.message || '获取验证码失败'
                }
            } catch (err) {
                console.error('获取验证码失败:', err)
                tipText.value = '获取验证码失败，请稍后重试'
            } finally {
                loading.value = false
            }
        }

        /**
         * 验证滑块位置与轨迹
         */
        const verifySlider = async () => {
            if (!token.value || tracks.value.length < 3) {
                refresh()
                return
            }

            loading.value = true

            try {

                const params = {
                    phone: props.phone,
                    token: token.value,
                    // sliderX即为拼图内容的前端坐标（left: sliderX-6 的-6与内容偏移+6抵消）
                    // 只需缩放回后端300px坐标系
                    x: String(Math.round(sliderX.value * 300 / 308)),
                    tracks: JSON.stringify(tracks.value),
                }
               

                const res = await proxy.$http.post('/core/base/a/sliderCapt/verify', params)

                if (res.data.state === 0 && res.data.result && res.data.result.success) {
                    verifyStatus.value = 'success'
                    tipText.value = '验证成功'
                    setTimeout(() => {
                        emit('success', token.value)
                    }, 500)
                } else {
                    const msg = (res.data.result && res.data.result.message) || res.data.message || '验证失败，请重试'
                    handleVerifyFail(msg)
                }
            } catch (err) {
                console.error('验证失败:', err)
                handleVerifyFail('验证失败，请重试')
            } finally {
                loading.value = false
            }
        }

        /**
         * 验证失败处理
         */
        const handleVerifyFail = (msg) => {
            verifyStatus.value = 'fail'
            tipText.value = msg

            refreshCount.value++

            if (refreshCount.value >= maxRefresh) {
                tipText.value = '失败次数过多，请稍后重试'
                setTimeout(() => {
                    emit('close')
                }, 2000)
            } else {
                setTimeout(() => {
                    refresh()
                }, 1000)
            }

            emit('fail')
        }

        /**
         * 刷新验证码
         */
        const refresh = () => {
            if (refreshCount.value >= maxRefresh) {
                emit('close')
                return
            }
            getCaptcha()
        }

        /**
         * 关闭弹窗
         */
        const handleClose = () => {
            emit('close')
        }

        // ========== 滑动交互逻辑 ==========

        const onDragStart = (e) => {
            if (loading.value || verifyStatus.value === 'success') return

            isDragging.value = true
            verifyStatus.value = ''
            tipText.value = ''

            startX = e instanceof MouseEvent ? e.clientX : e.touches[0].clientX
            startTime = Date.now()

            tracks.value = [{ x: 0, y: 0, t: startTime }]

            document.addEventListener('mousemove', onDragMove)
            document.addEventListener('mouseup', onDragEnd)
            document.addEventListener('touchmove', onDragMove, { passive: false })
            document.addEventListener('touchend', onDragEnd)
        }

        const onDragMove = (e) => {
            if (!isDragging.value) return

            if (!(e instanceof MouseEvent)) {
                e.preventDefault()
            }

            const currentX = e instanceof MouseEvent ? e.clientX : e.touches[0].clientX
            const moveX = currentX - startX

            sliderX.value = Math.max(0, Math.min(moveX, maxSliderX.value))

            const lastTrack = tracks.value[tracks.value.length - 1]
            const now = Date.now()

            if (Math.abs(sliderX.value - lastTrack.x) > 2 || now - lastTrack.t > 30) {
                tracks.value.push({
                    x: sliderX.value,
                    y: 0,
                    t: now
                })
            }
        }

        const onDragEnd = () => {
            if (!isDragging.value) return

            isDragging.value = false

            document.removeEventListener('mousemove', onDragMove)
            document.removeEventListener('mouseup', onDragEnd)
            document.removeEventListener('touchmove', onDragMove)
            document.removeEventListener('touchend', onDragEnd)

            if (sliderX.value < 10) {
                sliderX.value = 0
                tracks.value = []
                return
            }

            verifySlider()
        }

        // 监听弹窗显示
        watch(() => props.visible, (val) => {
            if (val) {
                refreshCount.value = 0
                getCaptcha()
            }
        })

        onUnmounted(() => {
            document.removeEventListener('mousemove', onDragMove)
            document.removeEventListener('mouseup', onDragEnd)
            document.removeEventListener('touchmove', onDragMove)
            document.removeEventListener('touchend', onDragEnd)
        })

        return {
            bgImage, sliderImage, sliderY, token, loading,
            sliderX, maxSliderX, isDragging, tracks,
            verifyStatus, tipText, refreshCount,
            getCaptcha, verifySlider, handleVerifyFail,
            refresh, handleClose,
            onDragStart, onDragMove, onDragEnd
        }
    }
}
</script>

<template>
    <transition name="captcha-fade">
        <div class="slider-captcha" v-if="visible">
            <!-- 遮罩层 -->
            <div class="captcha-mask" @click="handleClose"></div>

            <!-- 验证码主体 -->
            <div class="captcha-box">
                <!-- 头部 -->
                <div class="captcha-header">
                    <span class="title">安全验证</span>
                    <span class="close-btn" @click="handleClose">&times;</span>
                </div>

                <!-- 内容区 -->
                <div class="captcha-content">
                    <!-- 图片验证区 -->
                    <div class="image-area" v-loading="loading">
                        <!-- 背景图 -->
                        <img
                            v-if="bgImage"
                            :src="'data:image/png;base64,' + bgImage"
                            class="bg-img"
                            draggable="false"
                        />
                        <!-- 滑块图 -->
                        <img
                            v-if="sliderImage"
                            :src="'data:image/png;base64,' + sliderImage"
                            class="slider-img"
                            :style="{ top: sliderY + 'px', left: (sliderX - 6) + 'px' }"
                            draggable="false"
                        />

                        <!-- 加载占位 -->
                        <div v-if="!bgImage && loading" class="image-placeholder">
                            加载中...
                        </div>

                        <!-- 刷新按钮 -->
                        <div class="refresh-btn" @click="refresh" v-if="bgImage">
                            <svg viewBox="0 0 1024 1024" width="16" height="16">
                                <path d="M918.4 512c0-224-179.2-403.2-403.2-403.2S112 288 112 512c0 172.8 108.8 320 262.4 377.6 6.4 3.2 12.8 0 16-6.4l19.2-38.4c3.2-6.4 0-12.8-6.4-16C268.8 780.8 176 656 176 512c0-185.6 153.6-339.2 339.2-339.2S854.4 326.4 854.4 512c0 144-92.8 268.8-224 316.8V704c0-6.4-6.4-12.8-12.8-12.8h-51.2c-6.4 0-12.8 6.4-12.8 12.8v217.6c0 6.4 6.4 12.8 12.8 12.8h217.6c6.4 0 12.8-6.4 12.8-12.8v-51.2c0-6.4-6.4-12.8-12.8-12.8h-83.2C825.6 806.4 918.4 672 918.4 512z"/>
                            </svg>
                        </div>
                    </div>

                    <!-- 滑动条区域 -->
                    <div class="slider-area">
                        <!-- 滑道 -->
                        <div class="slider-track">
                            <!-- 已滑动填充 -->
                            <div
                                class="slider-fill"
                                :class="{
                  'fill-success': verifyStatus === 'success',
                  'fill-fail': verifyStatus === 'fail'
                }"
                                :style="{ width: sliderX + 'px' }"
                            ></div>

                            <!-- 提示文字 -->
                            <div class="track-tip" v-if="!isDragging && sliderX === 0">
                                <span>向右拖动滑块完成验证</span>
                            </div>
                        </div>

                        <!-- 滑块按钮 -->
                        <div
                            class="slider-thumb"
                            :class="{
                'thumb-dragging': isDragging,
                'thumb-success': verifyStatus === 'success',
                'thumb-fail': verifyStatus === 'fail'
              }"
                            :style="{ left: sliderX + 'px' }"
                            @mousedown="onDragStart"
                            @touchstart.prevent="onDragStart"
                        >
                            <span class="thumb-icon" v-if="verifyStatus === ''">&#8594;</span>
                            <span class="thumb-icon" v-else-if="verifyStatus === 'success'">&#10003;</span>
                            <span class="thumb-icon" v-else-if="verifyStatus === 'fail'">&#10005;</span>
                        </div>
                    </div>

                    <!-- 提示信息 -->
                    <div class="captcha-tip" v-if="tipText" :class="{ 'tip-success': verifyStatus === 'success' }">
                        {{ tipText }}
                    </div>
                </div>
            </div>
        </div>
    </transition>
</template>

<style scoped lang="less">
.captcha-fade-enter-active,
.captcha-fade-leave-active {
    transition: opacity 0.3s ease;
}

.captcha-fade-enter-from,
.captcha-fade-leave-to {
    opacity: 0;
}

.slider-captcha {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
}

.captcha-mask {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
}

.captcha-box {
    position: relative;
    width: 340px;
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15);
    overflow: hidden;
}

.captcha-header {
    padding: 14px 16px;
    border-bottom: 1px solid #f0f0f0;
    display: flex;
    justify-content: space-between;
    align-items: center;

    .title {
        font-size: 15px;
        font-weight: 500;
        color: #333;
    }

    .close-btn {
        font-size: 22px;
        color: #999;
        cursor: pointer;
        line-height: 1;
        padding: 0 4px;

        &:hover {
            color: #333;
        }
    }
}

.captcha-content {
    padding: 16px;
}

.image-area {
    position: relative;
    width: 308px;
    height: 154px;
    margin: 0 auto;
    border-radius: 4px;
    overflow: hidden;
    background: #f5f5f5;

    .bg-img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
    }

    .slider-img {
        position: absolute;
        /* 使用图片原始尺寸52x52（后端SLIDER_SIZE + SLIDER_PADDING*2） */
        z-index: 2;
    }

    .image-placeholder {
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #999;
        font-size: 14px;
    }

    .refresh-btn {
        position: absolute;
        top: 8px;
        right: 8px;
        width: 28px;
        height: 28px;
        background: rgba(0, 0, 0, 0.5);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: background 0.2s;

        svg {
            fill: #fff;
        }

        &:hover {
            background: rgba(0, 0, 0, 0.7);
        }
    }
}

.slider-area {
    position: relative;
    width: 308px;
    height: 40px;
    margin: 16px auto 0;
}

.slider-track {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 40px;
    background: #f5f5f5;
    border-radius: 20px;
    overflow: hidden;

    .slider-fill {
        position: absolute;
        top: 0;
        left: 0;
        height: 100%;
        background: linear-gradient(90deg, #4CAF50, #8BC34A);
        border-radius: 20px;
        transition: background 0.3s;

        &.fill-success {
            background: linear-gradient(90deg, #52c41a, #73d13d);
        }

        &.fill-fail {
            background: linear-gradient(90deg, #ff4d4f, #ff7875);
        }
    }

    .track-tip {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        display: flex;
        align-items: center;
        justify-content: center;

        span {
            font-size: 13px;
            color: #999;
            user-select: none;
        }
    }
}

.slider-thumb {
    position: absolute;
    top: 0;
    width: 40px;
    height: 40px;
    background: #fff;
    border-radius: 50%;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    cursor: grab;
    display: flex;
    align-items: center;
    justify-content: center;
    user-select: none;
    transition: background 0.3s, box-shadow 0.2s;
    z-index: 3;

    .thumb-icon {
        font-size: 16px;
        color: #666;
        font-weight: bold;
    }

    &:hover {
        box-shadow: 0 2px 12px rgba(0, 0, 0, 0.25);
    }

    &.thumb-dragging {
        cursor: grabbing;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);

        .thumb-icon {
            color: #4CAF50;
        }
    }

    &.thumb-success {
        background: #52c41a;

        .thumb-icon {
            color: #fff;
        }
    }

    &.thumb-fail {
        background: #ff4d4f;

        .thumb-icon {
            color: #fff;
        }
    }
}

.captcha-tip {
    text-align: center;
    margin-top: 12px;
    font-size: 13px;
    color: #ff4d4f;
    min-height: 20px;

    &.tip-success {
        color: #52c41a;
    }
}
</style>
